// Backend does not yet expose a single /dashboard endpoint. We synthesise the
// summary client-side from /databases + /jobs + /storages. When the backend
// ships a real endpoint, swap the body back to `api<DashboardSummary>('/dashboard')`.
import { useQuery } from '@tanstack/react-query';
import { api } from './client';
function describeSchedule(cron) {
    if (cron === '0 */4 * * *')
        return 'every 4h';
    if (cron === '0 */6 * * *')
        return 'every 6h';
    if (cron === '0 */12 * * *')
        return 'every 12h';
    if (cron === '0 */2 * * *')
        return 'every 2h';
    if (cron === '0 0 * * *')
        return 'daily';
    return cron;
}
export function useDashboard() {
    return useQuery({
        queryKey: ['dashboard'],
        // 30s for the home summary. This view is overview-only — not "watching
        // a live job", that's /jobs with its own live toggle. Each tick fans out
        // 4 parallel requests so dropping the cadence here pays off the most.
        refetchInterval: 30_000,
        refetchIntervalInBackground: false,
        queryFn: async () => {
            const [dbs, jobs, storages, backups] = await Promise.all([
                api('/databases'),
                api('/jobs?limit=200'),
                api('/storages'),
                api('/backups?limit=200&offset=0'),
            ]);
            const total_db = dbs.total;
            const healthy_count = dbs.items.filter((d) => d.enabled && (d.last_backup_status === 'ok' || d.last_backup_status === null)).length;
            let ok = 0;
            let partial = 0;
            let failed = 0;
            let running = 0;
            let pending = 0;
            for (const j of jobs.items) {
                if (j.status === 'succeeded')
                    ok += 1;
                else if (j.status === 'partial')
                    partial += 1;
                else if (j.status === 'failed')
                    failed += 1;
                else if (j.status === 'running')
                    running += 1;
                else if (j.status === 'queued' || j.status === 'retrying')
                    pending += 1;
            }
            const total_bytes = storages.items.reduce((a, s) => a + (s.total_bytes ?? 0), 0);
            // Bytes written in the last 24h — sums ciphertext_size_bytes of every
            // backup that finished within the last 24h regardless of status (failed
            // backups have null size and contribute 0). Multiple storages multiply
            // bytes-on-disk so the user's mental model of "how much my storage
            // bucket grew today" matches the sum × locations_count. For now we just
            // show per-backup size; tweak when bucket-level metrics are wired.
            const dayAgo = Date.now() - 86_400_000;
            const trend_bytes_per_day = backups.items.reduce((a, b) => {
                const finishedAt = b.finished_at ? new Date(b.finished_at).getTime() : 0;
                if (finishedAt < dayAgo)
                    return a;
                return a + (b.ciphertext_size_bytes ?? 0);
            }, 0);
            const databases = dbs.items.map((d) => ({
                id: d.id,
                name: d.name,
                size_bytes: d.size_bytes ?? 0,
                backups_stored: d.backups_stored ?? 0,
                last_backup_at: d.last_backup_at ?? null,
                storages: d.storages ?? [],
                schedule_label: describeSchedule(d.schedule_cron),
                status: !d.enabled
                    ? 'paused'
                    : (d.last_backup_status ?? 'ok'),
            }));
            return {
                healthy_count,
                total_db,
                backups_24h: { total: ok + partial + failed, ok, partial, failed },
                storage: {
                    total_bytes,
                    trend_bytes_per_day,
                    storage_count: storages.total,
                },
                queue: { running, pending },
                databases,
            };
        },
    });
}
